import type { SectorTicks } from '@cars/shared';

const CHECKPOINT_COLORS = [0xff0000, 0x00ff00, 0x0000ff] as const;

export class LapTimer {
  private readonly lastLapTime = new Array<number>(CHECKPOINT_COLORS.length).fill(0);
  private readonly bestLapTime = new Array<number>(CHECKPOINT_COLORS.length).fill(0);
  private readonly lastSectorTicks: [number, number, number] = [0, 0, 0];
  private readonly bestSectorTicks: [number, number, number] = [0, 0, 0];
  private lastLapDuration = 0;
  private bestLapDuration = 0;
  private currentCheckpoint = 0;
  private startTime = 0;
  private lastCheckpointTime = 0;
  private clockOn = false;

  getCurrentCheckpointColor(): number {
    return CHECKPOINT_COLORS[this.currentCheckpoint];
  }

  check(timeMs: number): { lapFinished: boolean; lapTimeMs?: number; bestTimeMs?: number } {
    if (!this.clockOn) {
      this.clockOn = true;
      this.startTime = timeMs;
      this.lastCheckpointTime = timeMs;
      this.nextCheck();
      return { lapFinished: false };
    }

    const segmentTime = timeMs - this.lastCheckpointTime;
    const sectorIndex = (this.currentCheckpoint + 2) % CHECKPOINT_COLORS.length;
    this.lastLapTime[sectorIndex] = segmentTime;
    this.lastSectorTicks[sectorIndex] = Math.round(segmentTime / (1000 / 60));

    const best = this.bestLapTime[sectorIndex];
    const isBestSegment = best === 0 || segmentTime < best;
    if (isBestSegment) {
      this.bestLapTime[sectorIndex] = segmentTime;
      this.bestSectorTicks[sectorIndex] = this.lastSectorTicks[sectorIndex];
    }

    this.lastCheckpointTime = timeMs;
    if (sectorIndex === 2) {
      const lapTimeMs = timeMs - this.startTime;
      this.startTime = timeMs;
      const response = {
        lapFinished: true,
        lapTimeMs,
        bestTimeMs:
          this.bestLapDuration || this.bestLapTime[0] + this.bestLapTime[1] + this.bestLapTime[2],
      };
      this.lastLapDuration = lapTimeMs;
      if (this.bestLapDuration === 0 || lapTimeMs < this.bestLapDuration)
        this.bestLapDuration = lapTimeMs;
      this.nextCheck();
      return response;
    }

    this.nextCheck();
    return { lapFinished: false };
  }

  getCurrentTime(nowMs: number): number {
    return this.clockOn ? nowMs - this.startTime : 0;
  }

  getCurrentTimeAtSimulationTick(tick: number, stepMs = 1000 / 60): number {
    return this.getCurrentTime(tick * stepMs);
  }

  checkAtSimulationTick(tick: number, stepMs = 1000 / 60): ReturnType<LapTimer['check']> {
    return this.check(tick * stepMs);
  }

  getBestLapTime(): number {
    return this.bestLapDuration;
  }

  getLastLapTime(): number {
    return this.lastLapDuration;
  }

  getLastSectorTicks(): SectorTicks {
    return [...this.lastSectorTicks] as SectorTicks;
  }

  getBestSectorTicks(): SectorTicks {
    return [...this.bestSectorTicks] as SectorTicks;
  }

  reset(): void {
    this.clockOn = false;
    this.currentCheckpoint = 0;
    this.startTime = 0;
    this.lastLapTime.fill(0);
    this.bestLapTime.fill(0);
    this.lastLapDuration = 0;
    this.bestLapDuration = 0;
    this.lastSectorTicks.fill(0);
    this.bestSectorTicks.fill(0);
  }

  private nextCheck(): void {
    this.currentCheckpoint = (this.currentCheckpoint + 1) % CHECKPOINT_COLORS.length;
  }
}
